/**
 * The ASK screen's body, shared by both trees (3056, docs/JOIN-LINKS.md).
 *
 * NOT PORTED FROM A MIRROR, because there is none — the design prototype has
 * no join-link flow at all, the same position `JoinCard` is in. Built from
 * Marcus's words (2026-08-30) and owes a DESIGN-CHANGELOG entry upstream; the
 * type scale, colours and caps idiom are `JoinCard`'s, so the two screens a
 * newcomer might land on read as one product.
 *
 * TWO STEPS ON ONE SCREEN. Address first — so the code is in flight while they
 * fill in the rest, and so a typo in the address costs one step rather than the
 * whole form. `v.codeSent` is the hinge.
 *
 * The chrome differs between trees, so the questions live here once and each
 * tree wraps them.
 */

import type { CSSProperties } from 'react';

import { useQd } from '../store/index.ts';
import { askVM } from '../store/selectors.ts';
import { PixelButton } from './PixelButton.tsx';
import type { JoinMetrics } from './JoinCard.tsx';

const MONO = 'var(--gt-font-mono)';

const FIELD = (m: JoinMetrics): CSSProperties => ({
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: m.fieldFs,
  padding: '12px 11px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

export function AskCard({ m }: { m: JoinMetrics }): JSX.Element {
  const s = useQd();
  const v = askVM(s);

  if (v.loading) {
    return (
      <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted)', padding: m.pad }}>
        READING THAT LINK…
      </div>
    );
  }

  /* Revoked, never issued, or a party deleted since — ONE message, because none
     of the three is actionable and telling them apart would only help somebody
     probing tokens. */
  if (!v.live) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: m.pad }}>
        <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
          {v.closedTxt}
        </div>
        <PixelButton label="LOG IN" onClick={() => s.nav('login')} fs={m.btnFs} pad="14px 16px" center nowrap />
      </div>
    );
  }

  /* Already in. Offer the party rather than inviting them to ask for something
     they have — the server told us, so this is not a guess. */
  if (v.alreadyIn) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: m.pad }}>
        <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
          {v.alreadyInTxt}
        </div>
        <PixelButton label={v.openLbl} onClick={() => s.nav('tonight')} fs={m.btnFs} pad="14px 16px" center nowrap />
      </div>
    );
  }

  const head = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
        {v.headline}
      </div>
      {v.leaderTxt ? (
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted)', letterSpacing: '.05em' }}>
          {v.leaderTxt}
        </div>
      ) : null}
    </div>
  );

  const err = v.hasErr ? (
    <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--acc2)', lineHeight: 1.7 }}>
      {v.askErr}
    </div>
  ) : null;

  /* SIGNED IN: one tap, no fields, no code. Existing accounts are verified by
     decision — see docs/JOIN-LINKS.md §3.3. Adding an address check here is the
     obvious "fix" for somebody who finds the gap later, and it is not one. */
  if (v.askAsMe) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: m.pad }}>
        {head}
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.8 }}>
          {v.gateHint}
        </div>
        {err}
        <PixelButton
          label={v.submitLbl}
          busy={v.busy}
          onClick={() => void s.submitAsk()}
          fs={m.btnFs}
          pad="14px 16px"
          center
          nowrap
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: m.pad }}>
      {head}

      {/* STEP ONE — the address. Locked once a code is out, with SEND ANOTHER
          CODE as the way back, because a typo here is the likeliest mistake on
          the screen and re-asking is the only repair. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <input
          value={v.askEmail}
          onChange={(e) => s.setAskForm({ askEmail: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.repeat && !v.codeSent) void s.sendAskCode();
          }}
          type="email"
          autoComplete="email"
          placeholder="EMAIL ADDRESS"
          readOnly={v.codeSent && !v.known}
          style={{ ...FIELD(m), ...(v.codeSent ? { color: 'var(--gt-muted)' } : null) }}
        />
        {v.known ? (
          /* THE ADDRESS IS ALREADY AN ACCOUNT, caught HERE rather than after a
             code was spent (Marcus, 2026-08-30). The shape changes rather than
             gaining a red line: there is nothing to retype, so the only
             control is the door that helps — and signing in brings them
             straight back to this screen. */
          <>
            <span style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--acc2)', lineHeight: 1.8 }}>
              {v.knownTxt}
            </span>
            <span style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted)', lineHeight: 1.8 }}>
              {v.knownHint}
            </span>
            <PixelButton
              label={v.knownLbl}
              onClick={() => s.nav('login')}
              fs={m.btnFs}
              pad="14px 16px"
              center
              nowrap
            />
          </>
        ) : !v.codeSent ? (
          <>
            <span style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.8 }}>
              {v.emailHint}
            </span>
            {err}
            <PixelButton
              label={v.sendLbl}
              busy={v.busy}
              onClick={() => void s.sendAskCode()}
              fs={m.btnFs}
              pad="14px 16px"
              center
              nowrap
            />
          </>
        ) : (
          <span style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted)', lineHeight: 1.8 }}>
            {v.sentTxt} · {v.expiryTxt}
          </span>
        )}
      </div>

      {/* STEP TWO — the code and the account. */}
      {v.codeSent ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input
            value={v.askCode}
            onChange={(e) => s.setAskForm({ askCode: e.target.value })}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-DIGIT CODE"
            style={{ ...FIELD(m), letterSpacing: '.3em' }}
          />
          <input
            value={v.askName}
            onChange={(e) => s.setAskForm({ askName: e.target.value })}
            placeholder="YOUR NAME"
            style={FIELD(m)}
          />
          <input
            value={v.askPw}
            onChange={(e) => s.setAskForm({ askPw: e.target.value })}
            type="password"
            autoComplete="new-password"
            placeholder="PASSWORD"
            style={FIELD(m)}
          />
          <input
            value={v.askPw2}
            onChange={(e) => s.setAskForm({ askPw2: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.repeat) void s.submitAsk();
            }}
            type="password"
            autoComplete="new-password"
            placeholder="PASSWORD AGAIN"
            style={FIELD(m)}
          />
          <span
            style={{
              fontFamily: MONO,
              fontSize: m.bodyFs,
              color: v.pwMismatch ? 'var(--acc2)' : 'var(--gt-muted-2)',
              lineHeight: 1.8,
            }}
          >
            {v.pwMismatch ? 'PASSWORDS DO NOT MATCH' : v.pwHint}
          </span>
          <span style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.8 }}>
            {v.gateHint}
          </span>
          {err}
          <PixelButton
            label={v.submitLbl}
            busy={v.busy}
            onClick={() => void s.submitAsk()}
            fs={m.btnFs}
            pad="14px 16px"
            center
            nowrap
          />
          <span
            onClick={() => {
              if (!v.busy) void s.sendAskCode();
            }}
            style={{
              fontFamily: MONO,
              fontSize: m.bodyFs,
              color: 'var(--gt-muted)',
              letterSpacing: '.05em',
              cursor: v.busy ? 'progress' : 'pointer',
              textAlign: 'center',
            }}
          >
            {v.resendLbl}
          </span>
        </div>
      ) : null}

      <span
        onClick={() => s.nav('login')}
        style={{
          fontFamily: MONO,
          fontSize: m.bodyFs,
          color: 'var(--gt-muted)',
          letterSpacing: '.05em',
          cursor: 'pointer',
          textAlign: 'center',
        }}
      >
        {v.haveAccountTxt}
      </span>
    </div>
  );
}
